import { Icon, type IconDefinition } from '@vrplatform/icons';
import { Text } from '@mantine/core';
import {
  Center,
  Tooltip,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useMemo } from 'react';

type IssueProps = {
  issues?: number | string;
  status: 'warning' | 'error' | 'success' | 'disabled';
  tooltip?: string;
  shadow?: boolean;
  icon?: IconDefinition;
  iconSize?: number;
};

export const IssueBadge = ({
  issues,
  status,
  tooltip,
  shadow = true,
  icon: iconProp,
  iconSize = 12,
}: IssueProps) => {
  const { colors } = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  const color = useMemo(() => {
    if (status === 'error') return 'red';
    if (status === 'warning') return 'yellow';
    if (status === 'disabled') return 'gray';
    return 'green';
  }, [status]);

  const icon = useMemo<IconDefinition>(() => {
    if (iconProp) return iconProp;
    if (status === 'error') return 'CrossIcon';
    return 'CheckIcon';
  }, [iconProp, status]);

  return (
    <Tooltip label={tooltip} disabled={!tooltip} withArrow withinPortal>
      <Center
        sx={(theme) => ({
          backgroundColor: !shadow
            ? undefined
            : theme.colors[color][0] + (colorScheme === 'dark' ? '10' : ''),
          width: !issues ? 25 : undefined,
          paddingInline: !issues ? undefined : 5,
          height: 25,
          borderRadius: !issues ? '100%' : theme.radius.lg,
        })}
      >
        <Center
          sx={(theme) => ({
            backgroundColor: theme.colors[color][color === 'gray' ? 6 : 8],
            borderRadius: 20,
            padding: 2,
          })}
        >
          {status === 'warning' ? (
            <Center w={11} h={11}>
              <Text
                component="span"
                c="white"
                ta="center"
                sx={{
                  lineHeight: 0.8,
                }}
              >
                !
              </Text>
            </Center>
          ) : (
            <Center sx={{ width: 15, height: 15 }}>
              <Icon icon={icon} size={iconSize} color={'#fff'} />
            </Center>
          )}
        </Center>
        {!!issues && (
          <Text
            component="span"
            c={colors[color][8]}
            size="xs"
            display={'inline-block'}
            ml={rem(6)}
            fw={500}
            ta="center"
            sx={{
              lineHeight: 0.8,
            }}
          >
            {issues}
          </Text>
        )}
      </Center>
    </Tooltip>
  );
};
